import React from "react";
import useContentModal from "../../hooks/useContentModal";

export interface ContentModalContainerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  customHeaderContent?: React.ReactNode;
  testId?: string;
}

export const ContentModalContainer: React.FC<ContentModalContainerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  showCloseButton = true,
  children,
  footer,
  className = "",
  customHeaderContent,
  testId = "content-modal-container",
}) => {
  const { classes, isDesktop } = useContentModal();

  if (!isOpen) return null;

  return (
    <div
      className={`${classes.container} ${className}`}
      data-testid={testId}
      role="dialog"
      aria-modal="true"
    >
      {/* Header */}
      <div
        className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200 tw-bg-white tw-flex-shrink-0"
        data-testid={`${testId}-header`}
      >
        <div className="tw-flex tw-items-center tw-gap-4">
          {showCloseButton && (
            <button
              onClick={onClose}
              className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-rounded-lg hover:tw-bg-gray-100"
              aria-label="Close content"
              data-testid={`${testId}-close-button`}
            >
              <span className="material-symbols-outlined tw-text-2xl">
                arrow_back
              </span>
            </button>
          )}

          <div className="tw-flex tw-flex-col tw-flex-1">
            <h1
              className="tw-text-2xl tw-font-bold tw-text-gray-900"
              data-testid={`${testId}-title`}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className="tw-text-sm tw-text-gray-500 tw-mt-1"
                data-testid={`${testId}-subtitle`}
              >
                {subtitle}
              </p>
            )}
            {customHeaderContent && (
              <div className="tw-mt-2">{customHeaderContent}</div>
            )}
          </div>
        </div>
      </div>

      {/* Content - Scrollable Area */}
      <div
        className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-bg-gray-50"
        data-testid={`${testId}-content`}
      >
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div
          className="tw-p-6 tw-bg-white tw-border-t tw-border-gray-200 tw-flex-shrink-0"
          data-testid={`${testId}-footer`}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default ContentModalContainer;
